import Color from "color";
import { AA_LARGE_TEXT, AA_NORMAL_TEXT, readableTone } from "@/lib/theme/readable";
import type { Theme, ThemeSwatch, ThemeSwatchPercentage } from "@/lib/theme/type";

/**
 * The dark appearance, derived from whatever light theme a brand supplies.
 *
 * Deriving rather than authoring is the same decision `readable.ts` already makes, for the same
 * reason: `ThemeConfig.config` is free-form JSON and every white-label brand brings its own. A
 * hand-picked dark palette would be one brand's dark palette — Boomer's — worn by Fixt, Jongens van
 * de Bar and everyone after them. Here the brand's own hue survives the flip and only the luminance
 * is rebuilt.
 *
 * Five rules produce the ladders below, and they are what to argue with if a screen looks wrong:
 *
 * 1. **Elevation lifts.** On paper a raised surface casts a shadow and goes darker; on a dark screen
 *    it catches light and goes lighter. So `background` is the darkest thing in the app and every
 *    card, sheet and modal sits above it. Reversing this is what makes a dark theme look flat.
 * 2. **Nothing is pure black.** A `#000000` page has no room left underneath it for anything to recede
 *    into, and on OLED it smears visibly while scrolling. The base is a very dark grey, carrying a
 *    trace of the brand hue so the app still feels like itself.
 * 3. **Nothing is pure white either.** `#ffffff` text on a near-black surface haloes and reads as
 *    glare rather than as ink. The primary label stops a few percent short.
 * 4. **Saturation comes down as lightness goes up.** A fully saturated brand blue lightened to sit on
 *    black vibrates against it. Peak colour stays in the middle of each ramp, where the brand is
 *    recognisable, and tapers at both ends.
 * 5. **Contrast is measured, not eyeballed.** Every text role is passed through `readableTone` against
 *    the surfaces it can land on, so an unusual brand hue cannot quietly produce an unreadable
 *    label. Body text targets WCAG AA; the deliberately quiet roles (tertiary, placeholder) target
 *    the large-text norm, which is what they are used for.
 *
 * The ramp indices keep their light-mode meaning — 25 is still the subtlest step and 900 still the
 * strongest — so `bg-brand-50` stays a whisper and `text-neutral-900` stays ink. Only which end of
 * the luminance scale that lives on changes. That is what lets the existing screens flip without
 * being rewritten.
 */

/** Lightness (HSL, 0–100) per surface role, darkest first. See rule 1. */
const SURFACE_LIGHTNESS = {
  /** The page itself. Everything else sits on top of this. */
  background: 7,
  neutral: 9,
  /** Cards and rows — the first step up from the page. Also what `bg-white` resolves to. */
  primary: 12,
  modal: 14,
  secondary: 17,
  tertiary: 21,
} as const;

/** Lightness per text role. See rules 3 and 5. */
const TEXT_LIGHTNESS = {
  primary: 95,
  secondary: 73,
  tertiary: 57,
  placeholder: 45,
} as const;

/** Lightness per border role — visible against the surfaces above, never a bright line. */
const BORDER_LIGHTNESS = {
  neutral: 17,
  primary: 21,
  modal: 24,
  tertiary: 28,
  /** The emphasis border; light mode uses near-ink for this. */
  secondary: 62,
  inverted: 92,
} as const;

/** Where each ramp step lands on the luminance scale. 25 stays subtlest, 900 stays strongest. */
const RAMP_LIGHTNESS: Record<keyof ThemeSwatch, number> = {
  25: 10,
  50: 13,
  100: 17,
  200: 23,
  300: 31,
  400: 41,
  500: 52,
  600: 61,
  700: 69,
  800: 77,
  900: 85,
};

/**
 * The semantic ramps have a different shape: `10` and `20` are backgrounds, `80` and `100` are the
 * fill and icon tones. Only the two background steps get a fixed lightness — the two foreground
 * steps are measured instead, see {@link fillOn}.
 */
const SEMANTIC_BACKGROUND_LIGHTNESS = { 10: 15, 20: 23 } as const;

/** The starting point for a semantic fill, before it is lifted to clear its own label. */
const SEMANTIC_FILL_LIGHTNESS = 66;

/** How far `80` recedes from the final `100`. Enough to read as a softer fill, not as a new colour. */
const SEMANTIC_FILL_STEP = 0.18;

/**
 * What to build a ramp out of when a brand ships one we cannot read. Falling back to a hue alone is
 * not enough — a greyscale `error` ramp is a status that no longer signals anything — so each role
 * carries the saturation it needs to stay meaningful.
 */
const FALLBACK_PIGMENT = {
  brand: { hue: 208, chroma: 62 },
  red: { hue: 0, chroma: 70 },
  error: { hue: 4, chroma: 86 },
  warning: { hue: 38, chroma: 92 },
  success: { hue: 152, chroma: 64 },
} as const;

type Pigment = { hue: number; chroma: number };

/**
 * Everything here runs on the boot path for every brand, and the input is JSON we did not write. A
 * step can be missing, empty, or not a colour at all — none of that may throw, it may only fall
 * back. Same contract as `readable.ts`.
 */
function parse(color: unknown): Color | undefined {
  if (typeof color !== "string" || color.trim() === "") {
    return undefined;
  }

  try {
    return Color(color);
  } catch {
    return undefined;
  }
}

/** The hue and saturation of the first candidate that carries colour, or the role's fallback. */
function pigment(candidates: readonly unknown[], fallback: Pigment): Pigment {
  for (const candidate of candidates) {
    const color = parse(candidate);

    // A fully desaturated value has no hue worth carrying — `Color` reports 0 for it, which is red.
    if (color && color.saturationl() > 2) {
      return { hue: color.hue(), chroma: color.saturationl() };
    }
  }

  return fallback;
}

function tone(hue: number, lightness: number, saturation: number): string {
  return Color.hsl(hue, Math.max(0, Math.min(100, saturation)), lightness)
    .hex()
    .toLowerCase();
}

/**
 * The most saturation any single step is allowed to carry. A brand ramp that runs at full chroma is
 * legible on paper and glaring on a dark screen — the mid step comes out as pure signal light. This
 * is the ceiling; {@link rampSaturation} does the shaping under it.
 */
const MAX_CHROMA = 80;

/**
 * Peak colour in the middle of a ramp, tapering at both ends. Rule 4: the same saturation that
 * looks right at 50% lightness turns into neon at 85% and mud at 10%.
 */
function rampSaturation(chroma: number, lightness: number): number {
  const distanceFromMiddle = Math.abs(lightness - 50) / 50;
  return Math.min(chroma, MAX_CHROMA) * (1 - 0.45 * distanceFromMiddle);
}

/**
 * The greys are not neutral: they carry a trace of the brand so a Fixt night and a Boomer night are
 * not the same night. Capped, because a saturated brand would otherwise tint the whole page.
 */
function neutralSaturation(chroma: number): number {
  return Math.min(chroma * 0.16, 12);
}

function inkSaturation(chroma: number): number {
  return Math.min(chroma * 0.12, 9);
}

/**
 * Lift a fill until the inverted label on top of it is readable.
 *
 * Lightness is not luminance: at the same HSL lightness a blue carries roughly a fifth of the light
 * a yellow does. A fixed ladder therefore produces a perfectly readable amber chip and an
 * unreadable blue button from the same number — which is exactly what a hue sweep across the wheel
 * found. So the fills are measured rather than placed, and a brand that needs no lift does not get
 * one.
 */
function fillOn(base: string, label: string): string {
  return readableTone(base, [label], AA_LARGE_TEXT);
}

function swatch(hue: number, chroma: number): ThemeSwatch {
  return Object.entries(RAMP_LIGHTNESS).reduce((ramp, [step, lightness]) => {
    ramp[Number(step) as keyof ThemeSwatch] = tone(
      hue,
      lightness,
      rampSaturation(chroma, lightness),
    );
    return ramp;
  }, {} as ThemeSwatch);
}

function semanticSwatch(hue: number, chroma: number, label: string): ThemeSwatchPercentage {
  const fill = fillOn(
    tone(hue, SEMANTIC_FILL_LIGHTNESS, rampSaturation(chroma, SEMANTIC_FILL_LIGHTNESS)),
    label,
  );

  return {
    // The background steps stay muted: a saturated tint behind a label is a colour cast, not a
    // status.
    10: tone(hue, SEMANTIC_BACKGROUND_LIGHTNESS[10], chroma * 0.5),
    20: tone(hue, SEMANTIC_BACKGROUND_LIGHTNESS[20], chroma * 0.5),
    // Derived from the final `100` rather than placed on its own, so a lifted fill takes its softer
    // sibling with it and the ramp cannot cross over itself.
    80: Color(fill).mix(Color(label), SEMANTIC_FILL_STEP).hex().toLowerCase(),
    100: fill,
  };
}

export function deriveDarkTheme(light: Theme): Theme {
  const brand = pigment(
    [light.color?.brand?.[500], light.color?.txt?.brand, light.color?.button?.primary],
    FALLBACK_PIGMENT.brand,
  );

  const surface = (lightness: number) =>
    tone(brand.hue, lightness, neutralSaturation(brand.chroma));
  const border = (lightness: number) => tone(brand.hue, lightness, neutralSaturation(brand.chroma));
  const ink = (lightness: number) => tone(brand.hue, lightness, inkSaturation(brand.chroma));

  const surfaces = {
    background: surface(SURFACE_LIGHTNESS.background),
    neutral: surface(SURFACE_LIGHTNESS.neutral),
    primary: surface(SURFACE_LIGHTNESS.primary),
    modal: surface(SURFACE_LIGHTNESS.modal),
    secondary: surface(SURFACE_LIGHTNESS.secondary),
    tertiary: surface(SURFACE_LIGHTNESS.tertiary),
  };

  /**
   * The surfaces a label can land on. Rule 5 measures against all of them at once rather than
   * against the page alone, because a secondary label on a raised card is the tightest pairing in
   * the app and the one that fails first.
   */
  const textBackgrounds = [
    surfaces.background,
    surfaces.primary,
    surfaces.modal,
    surfaces.secondary,
    surfaces.tertiary,
  ];

  const brandRamp = swatch(brand.hue, brand.chroma);

  /** The brand as a foreground: lightened until it is readable on every surface it sits on. */
  const brandText = readableTone(brandRamp[600], textBackgrounds, AA_NORMAL_TEXT);
  /** The brand as a fill, lifted until the inverted label on top of it reads. */
  const brandFill = fillOn(brandRamp[500], surfaces.background);

  const red = pigment([light.color?.red?.[500], light.color?.error?.[100]], FALLBACK_PIGMENT.red);
  const error = pigment([light.color?.error?.[100]], FALLBACK_PIGMENT.error);
  const warning = pigment([light.color?.warning?.[100]], FALLBACK_PIGMENT.warning);
  const success = pigment([light.color?.success?.[100]], FALLBACK_PIGMENT.success);

  return {
    color: {
      // `black` and `white` are roles, not colours: the app uses `text-black` for ink and
      // `bg-white` for a raised card. Ink becomes light and the card becomes the first step above
      // the page — which is why `white` is `surface.primary` and not the page itself. Fifty-odd
      // `bg-white` cards flip correctly on that one line.
      black: ink(96),
      white: surfaces.primary,
      neutral: swatch(brand.hue, neutralSaturation(brand.chroma)),
      brand: brandRamp,
      // Ionic's `--ion-color-primary`, and the app's heading ink. Light mode uses near-black here,
      // so dark mode uses near-white — with `txt.inverted` as its contrast colour either way.
      primary: ink(90),
      secondary: brandText,
      tertiary: surfaces.secondary,
      placeholder: ink(32),
      txt: {
        primary: readableTone(ink(TEXT_LIGHTNESS.primary), textBackgrounds, AA_NORMAL_TEXT),
        secondary: readableTone(ink(TEXT_LIGHTNESS.secondary), textBackgrounds, AA_NORMAL_TEXT),
        // Tertiary and placeholder are meant to recede. Holding them to the body-text norm would
        // flatten the hierarchy into two identical greys, so they target the large-text norm —
        // which is what they are used for.
        tertiary: readableTone(ink(TEXT_LIGHTNESS.tertiary), textBackgrounds, AA_LARGE_TEXT),
        placeholder: readableTone(ink(TEXT_LIGHTNESS.placeholder), textBackgrounds, AA_LARGE_TEXT),
        /** What sits on a filled brand button: the page, so the button reads as cut out of it. */
        inverted: surfaces.background,
        brand: brandText,
      },
      surface: {
        background: surfaces.background,
        primary: surfaces.primary,
        secondary: surfaces.secondary,
        tertiary: surfaces.tertiary,
        neutral: surfaces.neutral,
        modal: surfaces.modal,
        /** Still the opposite of the page — a light block, used for contrast blocks and avatars. */
        inverted: ink(92),
        brand: {
          primary: tone(brand.hue, 16, Math.min(brand.chroma * 0.55, 45)),
          secondary: tone(brand.hue, 22, Math.min(brand.chroma * 0.55, 45)),
        },
      },
      bordercolor: {
        primary: border(BORDER_LIGHTNESS.primary),
        secondary: ink(BORDER_LIGHTNESS.secondary),
        tertiary: border(BORDER_LIGHTNESS.tertiary),
        inverted: ink(BORDER_LIGHTNESS.inverted),
        modal: border(BORDER_LIGHTNESS.modal),
        neutral: border(BORDER_LIGHTNESS.neutral),
        brand: brandFill,
      },
      button: {
        primary: brandFill,
        secondary: ink(64),
        tertiary: surfaces.tertiary,
      },
      red: swatch(red.hue, red.chroma),
      error: semanticSwatch(error.hue, error.chroma, surfaces.background),
      warning: semanticSwatch(warning.hue, warning.chroma, surfaces.background),
      success: semanticSwatch(success.hue, success.chroma, surfaces.background),
    },
  };
}
